/**
 * /ja/product-discovery — プロダクトディスカバリー concept page (JA).
 * Ties the transcription bridges to decisions. Links /ja/transcription +
 * /ja/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function JaProductDiscovery() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/product-discovery"
      hreflangLocales={['ja']}
      documentTitle="プロダクトディスカバリーとは｜手法と進め方 · Draftlize"
      documentDescription="プロダクトディスカバリーの手法と進め方を解説。ユーザーインタビューの発見を「追跡できる決定」に変えるまでを扱います。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · ディスカバリー</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            プロダクトディスカバリー——<br />
            <em>発見を決定につなぐ。</em>
          </h1>
          <p className="lede">
            プロダクトディスカバリーは、「何を作るべきか」を作る前に確かめる活動です。ユーザーインタビュー、観察、検証を通じて、思い込みを事実に変えていく。うまくいく現場でも弱点は同じ場所にあります——せっかくの<em>インサイトが、決定につながる前に消える</em>。このページはディスカバリーの手法と、発見を「追跡できる決定」に変えるところまでを扱います。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">ディスカバリーの手法。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>ユーザーインタビュー</strong>——課題と文脈を、答えではなく物語で聞く。</li>
            <li><strong>観察・行動データ</strong>——言うことではなく、することを見る。</li>
            <li><strong>検証（プロトタイプ・実験）</strong>——小さく作って仮説を試す。</li>
            <li><strong>統合</strong>——バラバラの発見を、意思決定に足るパターンにまとめる。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            インタビューは <a href="/ja/transcription" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>文字起こし</a> して素材化するところから。ただし文字起こしはゴールではなく、統合と決定への出発点です。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">弱点は「発見→決定」の間。<em>ここをつなぐ。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            ディスカバリーの成果は、インタビューの発言そのものではなく、そこから下した<em>決定</em>です。多くのチームは発見をスライドにまとめて満足し、決定とその理由がどこにも残らない。Draftlize は発言→決定→要求を一本の線でつなぎます。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">なぜインサイトは消えるのか</h3>
            <p>インタビューのハイライトは Notion のページや議事録に散らばり、次のスプリントには誰も見返しません。決定の「なぜ」が発見と切り離されるからです。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">発言 → 決定 → 要求をつなぐ</h3>
            <p>Draftlize では、ユーザーの一言を、それが生んだ決定と、そこから導かれた要求にリンクします。後の検証がその前提を覆すと、依存先が自動で stale に。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI エージェントが発見を踏まえる</h3>
            <p>ディスカバリーの決定は MCP 経由で Claude Code・Cursor が読むコンテキストに。次の仕様は「ユーザーがこう言った、だからこう決めた」を踏まえて書けます。詳しくは <a href="/ja/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>意思決定ログ</a>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ディスカバリーの価値は、集めたインサイトの量ではない。そこから下した決定が、半年後もまだ辿れるかどうかだ。
        </blockquote>
        <cite>発見を決定につなぎ、前提が変わったら知らせる。それが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'ディスカバリーとデリバリーの違いは？', a: 'ディスカバリーは「正しいものを作るか」を確かめる活動、デリバリーは「正しく作る」活動です。両輪ですが、ディスカバリーの決定が残らないとデリバリーが的を外します。' },
            { q: 'インタビューは何件必要？', a: '厳密な数より、新しい発見が出なくなる（飽和する）まで、が目安です。大事なのは件数より、各発言をどの決定に結びつけたかです。' },
            { q: '発見をどう統合する？', a: '発言をテーマごとにまとめ、意思決定に足るパターンを見つけます。Draftlize なら各パターンを決定カードにし、元の発言にリンクできます。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">インタビューの一言を、決定につなぐ<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            ディスカバリーの発見を、Draftlize で発言→決定→要求のカードに。前提が変われば自動で stale、AI エージェントも読めます。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
